import { useEffect, useState } from "react";
import { api, gbp } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import { BookingDialog } from "@/components/BookingDialog";
import { Button } from "@/components/ui/button";
import { Loader2, Check, Moon, Building2, CalendarDays, Plane, ShieldCheck } from "lucide-react";

const fmtDate = (s) => {
  if (!s) return "";
  try { return new Date(s).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return s; }
};

export default function Hajj() {
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { api.get("/hajj").then(({ data }) => setPacks(data)).finally(() => setLoading(false)); }, []);

  return (
    <div>
      <PageHero
        title="Hajj 2026 packages"
        subtitle="Fully guided Hajj with confirmed 2026 departures — reserve your place today with a small deposit."
        image="https://images.unsplash.com/photo-1592326871020-04f58c1a52f3"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Trust bar */}
        <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-8 text-sm text-slate-600">
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#00b67a]" /> Authorised Hajj travel</span>
          <span className="flex items-center gap-2"><CalendarDays className="w-4 h-4 text-[#328CC1]" /> Confirmed 2026 departures</span>
          <span className="flex items-center gap-2"><Plane className="w-4 h-4 text-[#FF6B35]" /> Return flights included</span>
          <span className="flex items-center gap-2"><Check className="w-4 h-4 text-[#00b67a]" /> Deposit secures your seat</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#0B3C5D]" /></div>
        ) : (
          <div className="grid gap-6 md:grid-cols-3">
            {packs.map((p) => (
              <div key={p.id} data-testid={`hajj-card-${p.id}`} className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                <div className="relative h-52">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover" />
                  {p.featured && <span className="absolute top-3 left-3 bg-[#FF6B35] text-white rounded-full px-3 py-1 text-xs font-bold">Popular</span>}
                  <span className="absolute bottom-3 left-3 bg-black/60 text-white rounded-full px-2.5 py-1 text-xs flex items-center gap-1"><Moon className="w-3 h-3" /> {p.nights} nights</span>
                  {p.depart_date && (
                    <span className="absolute bottom-3 right-3 bg-white/95 text-slate-800 rounded-full px-2.5 py-1 text-xs font-medium flex items-center gap-1">
                      <CalendarDays className="w-3 h-3 text-[#0B3C5D]" /> {fmtDate(p.depart_date)}
                    </span>
                  )}
                </div>
                <div className="p-5 flex-1 flex flex-col">
                  <h3 className="font-display font-semibold text-lg text-slate-900">{p.title}</h3>
                  {p.depart_date && (
                    <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5">
                      <Plane className="w-3.5 h-3.5 text-[#FF6B35]" /> {p.airline} · {fmtDate(p.depart_date)} → {fmtDate(p.return_date)}
                    </p>
                  )}
                  <div className="text-xs text-slate-500 mt-2 space-y-1">
                    <p className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[#328CC1]" /> Makkah: {p.hotel_makkah}</p>
                    <p className="flex items-center gap-1.5"><Building2 className="w-3.5 h-3.5 text-[#328CC1]" /> Madinah: {p.hotel_madinah}</p>
                  </div>
                  <ul className="mt-3 space-y-1 flex-1">
                    {(p.includes || []).map((i) => <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-[#328CC1]" /> {i}</li>)}
                  </ul>
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-end justify-between">
                      <div>
                        <p className="text-[11px] text-slate-400">per person</p>
                        <p className="font-display text-2xl font-bold text-[#0B3C5D]">{gbp(p.price)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-[11px] text-slate-400">deposit from</p>
                        <p className="font-display text-lg font-bold text-[#FF6B35]">{gbp(p.deposit)}</p>
                      </div>
                    </div>
                    <BookingDialog productType="hajj" item={p} unitPrice={p.price}
                      trigger={<Button data-testid={`book-hajj-${p.id}`} className="w-full mt-3 rounded-full bg-[#FF6B35] hover:bg-[#e85a26] text-white font-semibold">Reserve with deposit</Button>} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
